import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramMotorFLA } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.electricalMotorFla.v1';

type Phase = '1' | '3';
type Fields = {
  horsepower: string;
  voltage: string;
  efficiency: string;
  powerFactor: string;
  phases: Phase;
  fullLoadAmps: string;
};
const EMPTY: Fields = {
  horsepower: '', voltage: '', efficiency: '', powerFactor: '', phases: '3', fullLoadAmps: '',
};

/**
 * Motor Full Load Amps — FLA = (HP × 746) / (k · V · Eff · PF),  k = 1 (1φ) or √3 (3φ)
 * Bidirectional: fill any 4 of {HP, V, Eff, PF, FLA} to solve the 5th. Phase is a selector.
 */
const ElectricalMotorFLAScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  const calculate = useCallback(() => {
    setError(null);
    const { horsepower, voltage, efficiency, powerFactor, fullLoadAmps } = fields;
    const filled = [horsepower, voltage, efficiency, powerFactor, fullLoadAmps].filter((s) => s !== '').length;
    if (filled < 4) { setError('Fill any 4 of {HP, V, Eff, PF, FLA} to solve the 5th.'); return; }
    if (filled === 5) { setError('All 5 values are filled — clear one to solve for it.'); return; }

    const k = fields.phases === '3' ? Math.sqrt(3) : 1;
    const hp = toImp(horsepower, 'power');
    const v = parseFloat(voltage);
    const eff = parseFloat(efficiency) / 100;
    const pf = parseFloat(powerFactor);
    const fla = parseFloat(fullLoadAmps);

    if (efficiency !== '' && (parseFloat(efficiency) <= 0 || parseFloat(efficiency) > 100)) {
      setError('Efficiency must be between 0 and 100%.'); return;
    }
    if (powerFactor !== '' && (pf <= 0 || pf > 1)) { setError('Power factor must be between 0 and 1.'); return; }

    if (fullLoadAmps === '') {
      if (!(hp > 0) || !(v > 0) || !(eff > 0) || !(pf > 0)) { setError('All inputs must be > 0.'); return; }
      setSolvedFor('fullLoadAmps');
      set({ fullLoadAmps: ((hp * 746) / (k * v * eff * pf)).toFixed(2) });
    } else if (horsepower === '') {
      if (!(v > 0) || !(eff > 0) || !(pf > 0) || !(fla > 0)) { setError('All other values must be > 0.'); return; }
      const hpSolved = (fla * k * v * eff * pf) / 746;
      setSolvedFor('horsepower');
      set({ horsepower: fromImp(hpSolved, 'power', 3) });
    } else if (voltage === '') {
      if (!(hp > 0) || !(eff > 0) || !(pf > 0) || !(fla > 0)) { setError('All other values must be > 0.'); return; }
      setSolvedFor('voltage');
      set({ voltage: ((hp * 746) / (k * fla * eff * pf)).toFixed(1) });
    } else if (efficiency === '') {
      if (!(hp > 0) || !(v > 0) || !(pf > 0) || !(fla > 0)) { setError('All other values must be > 0.'); return; }
      const effSolved = (hp * 746) / (k * v * fla * pf);
      if (effSolved <= 0 || effSolved > 1) { setError(`Computed efficiency ${(effSolved*100).toFixed(1)}% outside 0–100%.`); return; }
      setSolvedFor('efficiency');
      set({ efficiency: (effSolved * 100).toFixed(1) });
    } else if (powerFactor === '') {
      if (!(hp > 0) || !(v > 0) || !(eff > 0) || !(fla > 0)) { setError('All other values must be > 0.'); return; }
      const pfSolved = (hp * 746) / (k * v * fla * eff);
      if (pfSolved <= 0 || pfSolved > 1) { setError(`Computed PF ${pfSolved.toFixed(3)} outside (0,1].`); return; }
      setSolvedFor('powerFactor');
      set({ powerFactor: pfSolved.toFixed(3) });
    }
  }, [fields, toImp, fromImp, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  const result = useMemo(() => [
    fields.horsepower && { label: 'Power', value: fields.horsepower, unit: unit('power'), emphasis: solvedFor === 'horsepower' },
    fields.voltage && { label: 'Voltage', value: fields.voltage, unit: 'V', emphasis: solvedFor === 'voltage' },
    fields.efficiency && { label: 'Efficiency', value: fields.efficiency, unit: '%', emphasis: solvedFor === 'efficiency' },
    fields.powerFactor && { label: 'Power Factor', value: fields.powerFactor, emphasis: solvedFor === 'powerFactor' },
    fields.fullLoadAmps && { label: 'Full Load Amps', value: fields.fullLoadAmps, unit: 'A', emphasis: solvedFor === 'fullLoadAmps' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[], [fields, unit, solvedFor]);

  return (
    <CalcShell
      title="Motor Full Load Amps"
      subtitle="Solve for any variable — fill any 4 of 5"
      formula={
        <>
          <FormulaLine>1φ: FLA = (HP × 746) / (V × Eff × PF)</FormulaLine>
          <FormulaLine>3φ: FLA = (HP × 746) / (√3 × V × Eff × PF)</FormulaLine>
          <FormulaNote>FLA = Full Load Amps, HP in {unit('power')}, V = Voltage, Eff (%), PF (0..1).</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: FLA = (HP·746)/(k·V·Eff·PF)"
      diagram={<DiagramMotorFLA />}
      outputSlots={{
        bhp: fields.horsepower ? toImp(fields.horsepower, 'power') : undefined,
        volts: fields.voltage ? parseFloat(fields.voltage) : undefined,
        motor_efficiency_pct: fields.efficiency ? parseFloat(fields.efficiency) : undefined,
        power_factor: fields.powerFactor ? parseFloat(fields.powerFactor) : undefined,
        motor_fla: fields.fullLoadAmps ? parseFloat(fields.fullLoadAmps) : undefined,
        amps: fields.fullLoadAmps ? parseFloat(fields.fullLoadAmps) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.bhp != null && !fields.horsepower) patch.horsepower = fromImp(values.bhp, 'power', 2);
        if (values.volts != null && !fields.voltage) patch.voltage = values.volts.toFixed(1);
        if (values.motor_efficiency_pct != null && !fields.efficiency) patch.efficiency = values.motor_efficiency_pct.toFixed(1);
        if (values.power_factor != null && !fields.powerFactor) patch.powerFactor = values.power_factor.toFixed(3);
        if (values.motor_fla != null && !fields.fullLoadAmps) patch.fullLoadAmps = values.motor_fla.toFixed(2);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <SegmentedButtons
        value={fields.phases}
        onValueChange={(v) => set({ phases: v as Phase })}
        buttons={[
          { value: '1', label: 'Single Phase' },
          { value: '3', label: 'Three Phase' },
        ]}
        style={{ marginBottom: 16 }}
      />
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Power (${unit('power')})`} value={fields.horsepower} onChangeText={(v) => set({ horsepower: v })} rightText={unit('power')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Voltage" value={fields.voltage} onChangeText={(v) => set({ voltage: v })} rightText="V" maxDecimals={1} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Efficiency" value={fields.efficiency} onChangeText={(v) => set({ efficiency: v })} rightText="%" maxDecimals={1} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Power Factor" value={fields.powerFactor} onChangeText={(v) => set({ powerFactor: v })} maxDecimals={3} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Full Load Amps" value={fields.fullLoadAmps} onChangeText={(v) => set({ fullLoadAmps: v })} rightText="A" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default ElectricalMotorFLAScreen;
